import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { AppContext } from '@thd-nucleus/app-render';
import {
  DELETED_ACCOUNT_MSG,
  ADD_CHECKING_ACCOUNT,
} from '../../../constants/response-success-messages';
import { getNewPageNumber, hasNoPaymentCards } from '../../../utils/payments';
import { Pagination } from '../../shared/pagination/pagination';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { Alert } from '../../shared/alert/alert';
import { PaymentsHeader } from '../payments-header/payments-header';
import { CardType } from '../../../constants/payments';
import { CheckingAccountCard } from '../checking-account-card/checking-account-card';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { scrollToTop } from '../../../utils/general';
import { PaymentsHeaderSecondaryButtons } from '../payments-header/payments-header-secondary-buttons';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';

export const CheckingAccountList: React.FC = () => {
  const [deletedCreditCardIndex, setDeletedCreditCardIndex] = useState<
    number | null
  >(null);
  const {
    loading,
    checkingAccounts,
    paginationInfo,
    currentPageNumber,
    alertMessage,
    getPaymentCards,
    setAlertMessage,
  } = usePayments();
  const { channel } = useContext(ExperienceContext);
  const { instance } = useContext(AppContext);
  const isB2B = instance?.customer?.type === 'b2b';
  const isMobile = channel === 'mobile';

  const totalPages = Number(paginationInfo?.totalPages) || 0;

  useEffect(() => {
    return () => {
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = (paymentId: string, message: string) => {
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.SUCCESS,
      text: message,
    });
    if (message === DELETED_ACCOUNT_MSG.message) {
      const newPage = getNewPageNumber(
        currentPageNumber,
        totalPages,
        isB2B
          ? paginationInfo?.paymentsOnCurrentPage
          : checkingAccounts?.paymentCards.length,
      );
      // ensure user stays on the same page or on the previous page (if last payment deleted) to show delete confirmation
      onChangePage(newPage);
      return;
    }

    onChangePage(1);
  };

  const handleError = (paymentId: string, message: string) => {
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.ERROR,
      text: message,
    });
  };

  const onChangePage = (newPageNumber: number) => {
    triggerGetPaymentCards(newPageNumber);
  };

  const triggerGetPaymentCards = (pageNumber: number) => {
    return getPaymentCards(CardType.CHECKING_ACCOUNT, false, false, {
      page: pageNumber,
    });
  };

  const getCheckingAccountList = () => {
    const checkingAccountList =
      checkingAccounts?.paymentCards?.map((account, index) => (
        <div
          key={account.paymentId}
          className={`sui-flex ${isMobile ? 'sui-flex-col' : ''}`}
        >
          <CheckingAccountCard
            key={account.paymentId}
            card={account}
            setDeletedCreditCardIndex={setDeletedCreditCardIndex}
            index={index}
            isMobile={isMobile}
            onActionSuccess={handleSuccess}
            onActionFailure={handleError}
          />
        </div>
      )) || null;
    if (
      isMobile &&
      deletedCreditCardIndex !== null &&
      checkingAccountList !== null
    ) {
      checkingAccountList.splice(
        deletedCreditCardIndex,
        0,
        <>{successMessage}</>,
      );
    }
    return checkingAccountList;
  };

  const successMessage = alertMessage &&
    alertMessage.alertStatus === TAlertStatus.SUCCESS && (
      <div className="sui-mt-4 sm:sui-mt-0 lg:sui-mb-8">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  const successAddMessage = isMobile &&
    alertMessage?.text === ADD_CHECKING_ACCOUNT.message && (
      <div className="sui-mt-4 sm:sui-mt-0 lg:sui-mb-8">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  const desktopErrorMessage = !isMobile &&
    alertMessage &&
    alertMessage.alertStatus === TAlertStatus.ERROR && (
      <div className="lg:sui-mb-8">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  return (
    <>
      <Spinner active={loading} />
      <PaymentsHeader
        cardType={CardType.CHECKING_ACCOUNT}
        onActionFailure={handleError}
        onActionSuccess={handleSuccess}
      />
      {successAddMessage}
      {!isMobile && successMessage}
      {desktopErrorMessage}
      <PaymentsHeaderSecondaryButtons
        cardType={CardType.CHECKING_ACCOUNT}
        onActionSuccess={handleSuccess}
        onActionFailure={handleError}
      />
      {hasNoPaymentCards(checkingAccounts) && (
        <NoPaymentCardsMessage tenderType={CardType.CHECKING_ACCOUNT} />
      )}
      <div className="sui-grid sui-grid-cols-1 sui-gap-y-4 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3">
        {getCheckingAccountList()}
      </div>
      {totalPages > 1 && (
        <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
          <Pagination
            nPages={totalPages}
            onChangePage={(newPage) => {
              onChangePage(newPage);
              setAlertMessage(null);
              scrollToTop();
            }}
            page={currentPageNumber}
          />
        </div>
      )}
    </>
  );
};
